/**
 * useOpenWorkspaceFile — single entry point for "open this workspace file"
 * clicks fired from anywhere in the app (ChatPanel file cards, thought-block
 * file items, markdown workspace links).
 *
 * Responsibilities:
 *   1. Exit fullscreen chat (the workspace surface can't render behind it).
 *   2. Gracefully refuse when the user lacks WorkspacePage access
 *      (ivthub-only users don't have rocky-user/rocky-admin, so navigating
 *      them to `/?workspace=...` would dead-end on the AccessGate denial
 *      screen — instead we show a clear message pointing at the access
 *      request URL).
 *   3. If already on `/`, hand the path to WorkspaceNav via
 *      pendingNavigateKey + refreshWorkspace (no route change needed).
 *   4. Otherwise, deep-link through `/?workspace=<path>` so WorkspacePage's
 *      URL handler opens the file after the route change.
 *   5. Below desktop, collapse the chat once the file is really opening: there
 *      the chat is an overlay (full screen on phones) and would hide the file,
 *      so the click looked like it did nothing.
 */
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspaceNav } from '../context/WorkspaceNavContext';
import { useChatPanel } from '../context/ChatPanelContext';
import { ALLOWED_GROUPS, QUIP_TRIAL_URL, REQUEST_ACCESS_GROUPS } from '../components/AccessGate/AccessGate';
import { isCompact, useBreakpoint } from './useBreakpoint';

/**
 * The workspace file path in a `?workspace=<path>` link, or null when the URL is
 * not one. Shared by every place that turns such a link into an in-page open.
 */
export function workspacePathFromUrl(url: string): string | null {
  try {
    const match = url.match(/[?&]workspace=([^&]+)/);
    if (match) return decodeURIComponent(match[1]);
  } catch { /* ignore malformed URLs */ }
  return null;
}

export function useOpenWorkspaceFile() {
  const { t } = useTranslation('workspace');
  const navigate = useNavigate();
  const location = useLocation();
  const { ldap_groups } = useAuth();
  const { refreshWorkspace, setPendingNavigateKey } = useWorkspaceNav();
  const { exitFullscreen, chatCollapsed, toggleChat } = useChatPanel();
  const compact = isCompact(useBreakpoint());

  return useCallback((filePath: string) => {
    exitFullscreen();

    const canAccess = ldap_groups.some((g) => ALLOWED_GROUPS.includes(g));
    if (!canAccess) {
      window.alert(t('openFile.noAccess', { groups: REQUEST_ACCESS_GROUPS.join(' / '), url: QUIP_TRIAL_URL }));
      return;
    }

    if (compact && !chatCollapsed) toggleChat();

    if (location.pathname !== '/') {
      navigate(`/?workspace=${encodeURIComponent(filePath)}`);
      return;
    }
    setPendingNavigateKey(filePath);
    refreshWorkspace();
  }, [ldap_groups, exitFullscreen, compact, chatCollapsed, toggleChat, navigate, location.pathname, refreshWorkspace, setPendingNavigateKey, t]);
}
